import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const askChildRecordCallable = httpsCallable(functions, 'askChildRecord');
const summarizeAskConversationCallable = httpsCallable(functions, 'summarizeAskConversation');

const normalizeAskRecordResponse = (data = {}) => ({
  answer: typeof data.answer === 'string' ? data.answer : '',
  confidence: data.confidence || 'low',
  followUps: Array.isArray(data.followUps) ? data.followUps : [],
  sourceIds: Array.isArray(data.sourceIds) ? data.sourceIds : [],
  sources: Array.isArray(data.sources) ? data.sources : [],
  taskType: data.taskType || null,
});

const getAskRecordErrorMessage = (error = {}) => {
  const rawMessage = String(error?.message || '').trim();
  const rawCode = String(error?.code || '').trim();
  const detailsMessage = typeof error?.details?.message === 'string'
    ? error.details.message.trim()
    : '';

  if (detailsMessage) {
    return detailsMessage;
  }

  if (
    rawCode === 'functions/not-found'
    || rawCode === 'not-found'
    || rawMessage.toLowerCase() === 'not found'
  ) {
    return 'Ask is not available yet. Deploy the latest functions and try again.';
  }

  if (
    rawCode === 'functions/unauthenticated'
    || rawCode === 'unauthenticated'
  ) {
    return 'Please sign in again to ask about this record.';
  }

  if (
    rawCode === 'functions/permission-denied'
    || rawCode === 'permission-denied'
  ) {
    return 'You do not have access to ask about this child record.';
  }

  if (
    rawCode === 'functions/internal'
    || rawCode === 'internal'
    || rawMessage.toLowerCase() === 'internal'
  ) {
    return 'Ask hit a backend setup issue. Check that the latest functions are deployed and GEMINI_API_KEY is set.';
  }

  return rawMessage || 'Ask is not available right now.';
};

export const askChildRecord = async ({
  childId,
  conversation,
  question,
  sourceLimit,
  rankedSourceLimit,
} = {}) => {
  const normalizedChildId = String(childId || '').trim();
  const normalizedQuestion = String(question || '').trim();

  if (!normalizedChildId) {
    throw new Error('Choose a child before asking.');
  }
  if (!normalizedQuestion) {
    throw new Error('Type a question first.');
  }

  let response;
  try {
    response = await askChildRecordCallable({
      childId: normalizedChildId,
      conversation,
      question: normalizedQuestion,
      sourceLimit,
      rankedSourceLimit,
    });
  } catch (error) {
    throw new Error(getAskRecordErrorMessage(error));
  }

  if (response.data?.error) {
    throw new Error(response.data.error);
  }

  return normalizeAskRecordResponse(response.data);
};

export const summarizeAskConversation = async ({ childId, turns } = {}) => {
  try {
    const response = await summarizeAskConversationCallable({ childId, turns });
    if (response.data?.error) throw new Error(response.data.error);
    return {
      summary: typeof response.data?.summary === 'string' ? response.data.summary : '',
      sourceIds: Array.isArray(response.data?.sourceIds) ? response.data.sourceIds : [],
      sources: Array.isArray(response.data?.sources) ? response.data.sources : [],
    };
  } catch (error) {
    throw new Error(getAskRecordErrorMessage(error));
  }
};

export default askChildRecord;
